import Button from '2-core-elements/Buttons/Button';
import Input from '2-core-elements/FormElements/Input';
import Select from '2-core-elements/FormElements/SelectCustom';
import { useState, type FC } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

type SalaryContinuanceFormFields = {
    age: number;
    gender: 'male' | 'female';
    salary: number;
    percentage: number;
}

interface SalaryContinuanceFormReturn {
    age: number;
    gender: SalaryContinuanceFormFields['gender'];
    salary: number;
    percentageInsuredSalary: number;
}

interface SalaryContinuanceFormProps {
    ageOptions: {
        label: string;
        value: string;
    }[];
    maxInsuredPercentage?: number;
    labels?: {
        ageLabel?: string;
        genderLabel?: string;
        salaryLabel?: string;
        percentageLabel?: string;
        calculateLabel?: string;
    };
    onSubmit: (props: SalaryContinuanceFormReturn) => void;
}

const SalaryContinuanceCalcForm: FC<SalaryContinuanceFormProps> = ({ ageOptions, labels = {}, maxInsuredPercentage = 85.5, onSubmit }) => {
    const {
        ageLabel = 'Age*',
        genderLabel = 'Gender*',
        salaryLabel = 'Current annual salary*',
        percentageLabel = '% of Salary to insure*',
        calculateLabel = 'Calculate',
    } = labels;

    const genderOptions = [
        {
            label: 'Male',
            value: 'male',
        },
        {
            label: 'Female',
            value: 'female',
        }
    ];

    // manage input state
    const [selectedAge, setSelectedAge] = useState(ageOptions[0]);
    const [selectedGender, setSelectedGender] = useState(genderOptions[0]);

    const {
        handleSubmit,
        setValue,
        setError,
        getValues,
        clearErrors,
        formState: { errors },
    } = useForm<SalaryContinuanceFormFields>({
        defaultValues: {
            age: parseInt(selectedAge.value),
            salary: 0,
            percentage: maxInsuredPercentage,
            gender: 'male',
        },
    });

    const onFormSubmit: SubmitHandler<SalaryContinuanceFormFields> = data => {
        // data
        const { age, gender, percentage, salary } = data;

        if (salary <= 0) {
            setError('salary', { message: `Please input your salary` });
        }

        onSubmit({ age, gender, salary, percentageInsuredSalary: percentage });
    };

    return (
        <form className="form-calc" onSubmit={handleSubmit(onFormSubmit)}>
            <div className="form-group-calc">
                <label>{ageLabel}</label>
                {ageOptions && (
                    <Select
                        options={ageOptions}
                        value={selectedAge}
                        enableNull={false}
                        onChange={(option): void => {
                            if (option) {
                                setSelectedAge(option);
                                setValue('age', parseInt(option.value));
                            }
                        }}
                    />
                )}
            </div>
            <div className="form-group-calc">
                <label>{genderLabel}</label>
                <Select
                    options={genderOptions}
                    value={selectedGender}
                    enableNull={false}
                    onChange={(option): void => {
                        if (option) {
                            setSelectedGender(option);
                            setValue('gender', option.value as SalaryContinuanceFormFields['gender']);
                        }
                    }}
                />
            </div>
            <div className="form-group-calc">
                <label>{salaryLabel}</label>
                <NumericFormat
                    value={getValues('salary')}
                    defaultValue={0}
                    prefix="$"
                    allowNegative={false}
                    customInput={Input}
                    thousandSeparator=","
                    allowLeadingZeros={false}
                    debounceTimeout={300}
                    forcedKey="number-only"
                    fullWidth={true}
                    inputMode={`numeric`}
                    onValueChange={(values): void => {
                        const { floatValue } = values;

                        if (floatValue && floatValue <= 0) {
                            setError('salary', { message: `Please input value greater than 0` });
                        }

                        if (floatValue) {
                            setValue('salary', floatValue);
                            clearErrors('salary');
                        } else {
                            setError('salary', { message: `Please input your salary` });
                        }
                    }}
                />
                {errors.salary && (
                    <div className="input-message-error">{errors.salary.message}</div>
                )}

            </div>
            <div className="form-group-calc">
                <label>{percentageLabel}</label>
                <Input
                    placeholder="0"
                    value={getValues('percentage')}
                    type="number"
                    fullWidth={true}
                    className={`${errors.percentage && `is-error`}`}
                    onChange={({ target: { value } }): void => {
                        const val = parseFloat(value);
                        let isError = false;
                        let errorMessage = '';

                        if (isNaN(val)) {
                            isError = true;
                            errorMessage = 'Please input only number';
                        }

                        if (!isNaN(val) && val > maxInsuredPercentage) {
                            isError = true;
                            errorMessage = `Maximum percentage salary to insure is ${maxInsuredPercentage}%`;
                        }

                        if (isError) {
                            setError('percentage', { message: errorMessage });
                        } else {
                            setValue('percentage', val);
                            clearErrors('percentage');
                        }
                    }}
                />
                <div className="input-message">Up to {maxInsuredPercentage}% of your salary</div>
                {errors.percentage && (
                    <div className="input-message-error">{errors.percentage.message}</div>
                )}
            </div>
            <div className="form-group-calc">
                <Button type="submit" disabled={Object.keys(errors).length > 0}>{calculateLabel}</Button>
            </div>
        </form>
    );
};

export default SalaryContinuanceCalcForm;
